import { useCallback } from 'react';
import { Platform, View } from 'react-native';
import ToastLib, { BaseToast, BaseToastProps, ToastType } from 'react-native-toast-message';
import { CreateResponsiveStyle, DEVICE_SIZES, minSize } from 'rn-responsive-styles';

import { icons } from './icons';
import { IconButton } from '../IconButton';
import { CloseIcon } from '../../assets';
import { colors, fontFamilyBold, fontFamilyRegular, spacing, shadows } from '../../theme';

type Variant = ToastType | 'warning';
type Props = BaseToastProps & { type: Variant; onClose?: () => void };
export const ToastComponent = ({
	type,
	onClose,
	text1NumberOfLines = 2,
	text2NumberOfLines = 2,
	...props
}: Props) => {
	const styles = useStyles();
	const Icon = icons[type as keyof typeof icons] ?? icons.info;
	const borderColor = colors.toast[type as keyof typeof colors.toast] ?? colors.toast.info;

	const LeadingIcon = useCallback(
		() => (
			<View style={styles.leftIconContainer}>
				<Icon />
			</View>
		),
		[Icon, styles.leftIconContainer],
	);

	const TrailingIcon = useCallback(
		() => (
			<IconButton
				Icon={CloseIcon}
				appearance="ghost"
				style={styles.close}
				onPress={onClose ? onClose : ToastLib.hide}
			/>
		),
		[onClose, styles.close],
	);

	return (
		<BaseToast
			{...props}
			style={[styles.root, { borderColor, borderLeftColor: borderColor }]}
			contentContainerStyle={styles.content}
			text1Style={styles.title}
			text2Style={styles.description}
			text1NumberOfLines={text1NumberOfLines}
			text2NumberOfLines={text2NumberOfLines}
			renderLeadingIcon={LeadingIcon}
			renderTrailingIcon={TrailingIcon}
		/>
	);
};
ToastComponent.icons = icons;

const useStyles = CreateResponsiveStyle(
	{
		root: {
			backgroundColor: colors.white,
			height: 'auto',
			borderLeftWidth: 1,
			borderWidth: 1,
			borderRadius: 8,
			...Platform.select({
				web: {
					maxWidth: `calc(100vw - ${2 * spacing(2)})`,
					margin: 'auto',
				},
				default: {
					maxWidth: '100%',
				},
			}),
			...shadows.drop3,
		},
		content: {
			paddingVertical: spacing(3),
		},
		title: {
			fontFamily: fontFamilyBold,
			fontSize: 16,
			lineHeight: 20.8,
			letterSpacing: -0.16,
		},
		description: {
			fontFamily: fontFamilyRegular,
			fontSize: 14,
			lineHeight: 24.5,
			letterSpacing: 0.14,
		},
		close: {
			marginRight: spacing(),
			marginTop: spacing(),
		},
		leftIconContainer: {
			width: 24,
			height: 24,
			alignItems: 'center',
			justifyContent: 'center',
			marginLeft: spacing(3),
			marginTop: spacing(3),
		},
	},
	{
		[minSize(DEVICE_SIZES.MD)]: {
			root: {
				width: '100%',
				maxWidth: 480,
			},
		},
		[minSize(DEVICE_SIZES.LG)]: {
			root: {
				width: '100%',
				maxWidth: 640,
			},
		},
	},
);
